export const CASE_SCHEMA_ID = "logbookanaes.case" as const;
export const CASE_SCHEMA_VERSION = 2 as const;

export type CaseEntryOrigin = "native" | "external_import" | "developer_generated";
export type LogbookEntryKind = "anaestheticCase" | "regional";
export type ValueOrigin = "explicit" | "defaulted" | "learned" | "template" | "derived";

export interface ProcedureSupervisionDetail {
  supervision: string;
  grade: string;
  [extension: string]: unknown;
}

export interface RegionalTechniqueDetail {
  technique: string;
  catheter: string;
  supervision: string;
  needleUsed: string;
  localAnaesthetics: string[];
  volume: string;
  notes?: string;
  [extension: string]: unknown;
}

export interface StudioCaseExtension {
  archivedAt?: string | null;
  lastMutationId?: string;
  [extension: string]: unknown;
}

/**
 * The one canonical nested clinical record used by Studio.
 *
 * The index signature is intentional. A newer iOS/PWA release may add fields
 * before Studio understands them; read/edit/write must not erase those fields.
 */
export interface CanonicalCaseData {
  entry_type: LogbookEntryKind | string;
  access_new: string[];
  access_situ: string[];
  age: string;
  airway: string;
  airway_awake_status: string;
  anaesthetic_equipment: string;
  asa: string;
  breathing: string;
  complications: string[];
  duration: string;
  maint: string;
  notes: string;
  position: string;
  dayCase?: string;
  postop: string;
  procedure: string;
  regional: string[];
  role: string;
  spec: string;
  sup: string;
  supGrade: string;
  teaching: string[];
  urg: string;
  funding: string;
  insurer: string;
  proc_supervision: Record<string, ProcedureSupervisionDetail>;
  regional_detail: Record<string, RegionalTechniqueDetail>;
  customFields: Record<string, string>;
  valueOrigins: Record<string, ValueOrigin | string>;
  [extension: string]: unknown;
}

/**
 * Schema-v2 case envelope. Tombstones use the same in-memory shape with blank
 * clinical values; the Firestore encoder redacts those values on write.
 */
export interface CanonicalCase {
  id: string;
  caseId: string;
  date: string;
  session: string;
  hospital: string;
  data: CanonicalCaseData;
  createdAt: string;
  updatedAt: string;
  lastUpdated: string;
  isDeleted: boolean;
  deletedAt: string | null;
  deviceId: string;
  schemaId: typeof CASE_SCHEMA_ID;
  schemaVersion: number;
  sourceSystem: string;
  sourceWorksheet: string;
  sourceCaseID: string;
  rawPrimarySpecialty: string;
  rawSecondarySpecialty: string;
  normalizedPrimarySpecialty: string;
  normalizedSecondarySpecialty: string;
  rawImportRowsData: string;
  entry_origin: CaseEntryOrigin | string;
  importBatchId: string;
  importOriginalFilename: string;
  importedAt: string | null;
  studio?: StudioCaseExtension;
  [extension: string]: unknown;
}

/** Drafts are transient UI input only. They are never persisted as cases. */
export interface CaseDraft {
  date: string;
  session: string;
  hospital: string;
  age: string;
  asa: string;
  procedure: string;
  spec: string;
  urg: string;
  duration: string;
  role: string;
  maint: string;
  anaesthetic_equipment: string;
  breathing: string;
  airway: string;
  airway_awake_status: string;
  regional: string[];
  access_situ: string[];
  access_new: string[];
  position: string;
  dayCase: string;
  postop: string;
  complications: string[];
  sup: string;
  supGrade: string;
  teaching: string[];
  funding: string;
  insurer: string;
  notes: string;
  customFields: Record<string, string>;
  proc_supervision: Record<string, ProcedureSupervisionDetail>;
  regional_detail: Record<string, RegionalTechniqueDetail>;
  valueOrigins: Record<string, ValueOrigin | string>;
}

export function emptyCanonicalData(): CanonicalCaseData {
  return {
    entry_type: "anaestheticCase",
    access_new: [],
    access_situ: [],
    age: "",
    airway: "",
    airway_awake_status: "",
    anaesthetic_equipment: "",
    asa: "",
    breathing: "",
    complications: [],
    duration: "",
    maint: "",
    notes: "",
    position: "",
    postop: "",
    procedure: "",
    regional: [],
    role: "",
    spec: "",
    sup: "",
    supGrade: "",
    teaching: [],
    urg: "",
    funding: "",
    insurer: "",
    proc_supervision: {},
    regional_detail: {},
    customFields: {},
    valueOrigins: {},
  };
}
